import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react';
import {
  defaultAppTheme,
  themeModes,
  themeModeStorageKey,
  type AppTheme,
  type ThemeMode,
  type Theming
} from '@knack/asterisk-react';

import { useApplicationQuery } from '@/hooks/api/queries/useApplicationQuery';

type ThemingContextState = {
  theme: AppTheme;
  setTheme: (theme: AppTheme) => void;
  themeMode: ThemeMode | null;
  setThemeMode: (mode: ThemeMode) => void;
} | null;

type ThemeModeAppPreference = {
  appId: string;
  themeMode: ThemeMode;
};

// Utility functions for managing theme mode preferences per app
function getThemeModeAppPreferences(): ThemeModeAppPreference[] {
  try {
    const stored = localStorage.getItem(themeModeStorageKey);
    if (!stored) {
      return [];
    }

    // Try to parse as JSON first (new format)
    const parsed = JSON.parse(stored);

    // If it's already an array, return it
    if (Array.isArray(parsed)) {
      return parsed;
    }

    // If it's not an array, it might be the old single string format
    // Return empty array since we can't convert without knowing the appId
    return [];
  } catch {
    // If JSON.parse fails, it's likely the old string format
    // We can't convert it here without knowing the appId, so return empty array
    return [];
  }
}

function getThemeModeForApp(appId: string): ThemeMode | null {
  const preferences = getThemeModeAppPreferences();
  const preference = preferences.find((p) => p.appId === appId);
  return preference?.themeMode || null;
}

function migrateLegacyThemeMode(appId: string): ThemeMode | null {
  try {
    const stored = localStorage.getItem(themeModeStorageKey);
    if (!stored) {
      return null;
    }

    // Try to parse as JSON first
    try {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        // Already in new format, no migration needed
        return null;
      }
    } catch {
      // Not valid JSON, likely the old string format
    }

    // Handle old format: value can be either raw ("dark") or JSON-stringified ("\"dark\"")
    let rawMode: string;
    try {
      // If it was JSON-stringified, this unwraps the quotes
      rawMode = JSON.parse(stored);
    } catch {
      rawMode = stored;
    }

    const normalizedMode = (rawMode ?? '').toLowerCase() as ThemeMode;

    // Check if it's a valid ThemeMode
    if (themeModes.includes(normalizedMode)) {
      const validThemeMode = normalizedMode;

      // Migrate old format to new array format
      try {
        const newPreferences = [{ appId, themeMode: validThemeMode }];
        localStorage.setItem(themeModeStorageKey, JSON.stringify(newPreferences));
      } catch {
        // Ignore localStorage errors during migration
      }

      return validThemeMode;
    }

    return null;
  } catch {
    return null;
  }
}

function storeThemeModeForApp(appId: string, themeMode: ThemeMode): void {
  const preferences = getThemeModeAppPreferences();
  const existingIndex = preferences.findIndex((p) => p.appId === appId);

  if (existingIndex !== -1) {
    // Update existing preference
    preferences[existingIndex].themeMode = themeMode;
  } else {
    // Add new preference
    preferences.push({ appId, themeMode });
  }

  try {
    localStorage.setItem(themeModeStorageKey, JSON.stringify(preferences));
  } catch {
    // Silently ignore localStorage errors (e.g., QuotaExceededError, private browsing mode)
    // The app will continue to function with the current theme mode in memory
  }
}

const ThemingContext = createContext<ThemingContextState>(null);

export function ThemingContextProvider({
  theming,
  children
}: {
  theming: Theming;
  children: ReactNode;
}) {
  const { data: application } = useApplicationQuery();
  const [theme, setTheme] = useState<AppTheme>(defaultAppTheme);

  // Initialize theme mode from app-specific preferences or legacy global storage
  const [themeMode, setThemeMode] = useState<ThemeMode | null>(() => {
    if (!application) {
      return null;
    }

    // First, try to get app-specific theme mode
    const appSpecificThemeMode = getThemeModeForApp(application.id);
    if (appSpecificThemeMode) {
      return appSpecificThemeMode;
    }

    // Fallback to migrating legacy theme mode storage
    return migrateLegacyThemeMode(application.id);
  });

  useEffect(() => {
    const foundTheme = theming.appThemes.find(
      (themeItem) => themeItem.id === theming.activeThemeId
    );

    if (foundTheme && application) {
      const newThemeMode = themeMode ?? foundTheme.mode;
      setTheme({ ...foundTheme, mode: newThemeMode });

      // Update themeMode state if it was initially null to keep state synchronized
      if (themeMode === null) {
        setThemeMode(foundTheme.mode);
      }
    }
  }, [themeMode, theming, application]);

  // Custom setThemeMode that saves to app-specific preferences
  const handleSetThemeMode = useCallback(
    (mode: ThemeMode) => {
      if (application) {
        storeThemeModeForApp(application.id, mode);
        setThemeMode(mode);
      }
    },
    [application]
  );

  const contextValue = useMemo(
    () => ({
      theme,
      setTheme,
      themeMode,
      setThemeMode: handleSetThemeMode
    }),
    [theme, themeMode, handleSetThemeMode]
  );

  return <ThemingContext.Provider value={contextValue}>{children}</ThemingContext.Provider>;
}

export const useThemingContext = () => {
  const context = useContext(ThemingContext);
  if (!context) {
    throw new Error('useThemingContext must be used within a ThemingContextProvider');
  }
  return context;
};
